import React, { useState } from "react";
import { createPortal } from "react-dom";
import { ScrollText, ChevronDown, ChevronUp, ShieldCheck, UserRound } from "lucide-react";
import { colors, fonts } from "@/theme";
import { useAuth } from "@/store/useAuth";
import { ensureSession, trackEvent } from "@/api/client";
import { TOS_VERSION, TOS_LAST_UPDATED, TOS_STORAGE_KEY } from "@/data/terms";
import { TermsContent } from "./TermsContent";

const ACCENT = "#0A84FF";

// First screen users see: sign in / create account (with the Beta Terms) or explore as a guest.
// Guest access is gated on accepting the Terms; attempting it before agreeing gives an intuitive
// highlight on the consent box. Logged-in or returning (accepted) visitors skip this entirely.
export default function WelcomeGate() {
  const user = useAuth((s) => s.user);
  const openModal = useAuth((s) => s.openModal);

  const already = (() => {
    try { return localStorage.getItem(TOS_STORAGE_KEY) === TOS_VERSION; } catch { return false; }
  })();
  const [entered, setEntered] = useState(already);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [flash, setFlash] = useState(false);

  if (entered || user) return null;

  const flashConsent = () => { setFlash(true); setTimeout(() => setFlash(false), 700); };
  const gate = (fn) => () => { if (!agreed) { flashConsent(); return; } fn(); };

  const persistTos = () => {
    try {
      localStorage.setItem(TOS_STORAGE_KEY, TOS_VERSION);
      localStorage.setItem(`${TOS_STORAGE_KEY}_at`, new Date().toISOString());
    } catch { /* storage blocked — still allow this session */ }
  };

  const continueAsGuest = gate(() => {
    persistTos();
    ensureSession().then((s) =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/guest-tos`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "X-Session-Id": s.session_id, "X-Session-Token": s.token },
        body: JSON.stringify({ tos_version: TOS_VERSION }),
      }).catch(() => {})
    ).catch(() => {});
    trackEvent("guest_continue", {});
    setEntered(true);
  });

  const goLogin = gate(() => openModal("login"));
  const goSignup = gate(() => openModal("signup"));

  return createPortal(
    <div style={styles.overlay} data-testid="welcome-gate" role="dialog" aria-modal="true" aria-label="Welcome, sign in">
      <div style={styles.card}>
        <div style={styles.head}>
          <span style={styles.iconWrap}><ShieldCheck size={20} strokeWidth={2.2} color={ACCENT} /></span>
          <div>
            <div style={styles.brand}>Resource Circularity Engine</div>
            <div style={styles.title}>Sign in to save &amp; sync your work</div>
          </div>
        </div>

        <label style={{ ...styles.consent, ...(flash ? styles.consentFlash : {}) }} data-testid="welcome-consent">
          <input type="checkbox" checked={agreed} data-testid="welcome-consent-checkbox"
            onChange={(e) => setAgreed(e.target.checked)} style={styles.checkbox} />
          <span>I have read and agree to the <strong>Beta Terms of Service</strong> (v{TOS_VERSION} · {TOS_LAST_UPDATED}).</span>
        </label>

        <button type="button" data-testid="welcome-terms-toggle" onClick={() => setShowTerms((v) => !v)} style={styles.readBtn}>
          <ScrollText size={14} strokeWidth={2} />
          {showTerms ? "Hide the full Terms of Service" : "Read the full Terms of Service"}
          {showTerms ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showTerms && (<div style={styles.termsBox} data-testid="welcome-terms-full"><TermsContent /></div>)}

        <div style={styles.actions}>
          <div style={styles.row}>
            <button style={{ ...styles.emailBtn, ...(agreed ? {} : styles.dim) }} onClick={goLogin} data-testid="welcome-signin">Sign in</button>
            <button style={{ ...styles.emailBtnPrimary, ...(agreed ? {} : styles.dim) }} onClick={goSignup} data-testid="welcome-signup">Create account</button>
          </div>
        </div>

        <div style={styles.divider}><span style={styles.divLine} /><span style={styles.divText}>or</span><span style={styles.divLine} /></div>

        <button type="button" onClick={continueAsGuest} data-testid="welcome-guest-btn"
          style={{ ...styles.guestBtn, ...(agreed ? {} : styles.guestBtnLocked) }}
          title={agreed ? "" : "Accept the Terms above to explore as a guest"}>
          <UserRound size={15} strokeWidth={2} /> Continue as guest
        </button>
        <div style={styles.guestNote}>Guest work stays on this device only. Sign in to keep your scenarios and resources across devices.</div>
      </div>
    </div>,
    document.body
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 3500, display: "flex", alignItems: "center", justifyContent: "center",
    padding: 24, boxSizing: "border-box",
    background: "radial-gradient(1200px 600px at 50% -10%, rgba(81,170,254,0.20), rgba(18,26,36,0.62))",
    backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
  },
  card: {
    width: "min(440px, 96vw)", maxHeight: "92vh", overflowY: "auto", display: "flex", flexDirection: "column",
    background: "rgba(255,255,255,0.97)", borderRadius: 22, border: `1px solid ${colors.glassBorder || "rgba(255,255,255,0.6)"}`,
    boxShadow: "0 34px 90px rgba(0,0,0,0.34)", padding: "26px 26px 22px", boxSizing: "border-box",
    fontFamily: fonts.sans,
  },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  iconWrap: { flexShrink: 0, width: 42, height: 42, borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(10,132,255,0.10)", border: "1px solid rgba(10,132,255,0.22)" },
  brand: { fontSize: 10.5, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: colors.brand },
  title: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.15, marginTop: 2 },
  consent: { display: "flex", alignItems: "flex-start", gap: 9, padding: "11px 13px", borderRadius: 13, background: "rgba(10,132,255,0.05)", border: "1px solid rgba(10,132,255,0.18)", fontSize: 12, lineHeight: 1.45, color: colors.textPrimary, cursor: "pointer", transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease" },
  consentFlash: { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}33`, transform: "scale(1.015)" },
  checkbox: { marginTop: 2, width: 16, height: 16, accentColor: ACCENT, cursor: "pointer", flexShrink: 0 },
  readBtn: { display: "inline-flex", alignItems: "center", gap: 8, alignSelf: "flex-start", margin: "10px 0 4px", padding: "7px 13px", borderRadius: 999, border: `1px solid ${ACCENT}44`, background: `${ACCENT}0F`, color: ACCENT, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  termsBox: { maxHeight: "34vh", overflowY: "auto", margin: "6px 0 4px", padding: "10px 12px", borderRadius: 12, background: "rgba(0,0,0,0.02)", border: `1px solid ${colors.border}` },
  actions: { display: "flex", flexDirection: "column", gap: 9, marginTop: 14 },
  oauthBtn: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: "12px", borderRadius: 12, border: `1.5px solid ${colors.border}`, background: "#fff", fontSize: 13.5, fontWeight: 600, color: colors.textPrimary, cursor: "pointer" },
  row: { display: "flex", gap: 9 },
  emailBtn: { flex: 1, padding: "11px", borderRadius: 12, border: `1.5px solid ${colors.border}`, background: "#fff", fontSize: 13.5, fontWeight: 700, color: colors.textPrimary, cursor: "pointer" },
  emailBtnPrimary: { flex: 1, padding: "11px", borderRadius: 12, border: "none", background: colors.brand, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 18px rgba(81,170,254,0.4)" },
  dim: { opacity: 0.5 },
  divider: { display: "flex", alignItems: "center", gap: 10, margin: "16px 0 12px" },
  divLine: { flex: 1, height: 1, background: colors.border },
  divText: { fontSize: 11, color: colors.textTertiary },
  guestBtn: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", borderRadius: 12, border: `1.5px dashed ${colors.border}`, background: "transparent", fontSize: 13, fontWeight: 700, color: colors.textSecondary, cursor: "pointer", transition: "opacity 0.2s ease" },
  guestBtnLocked: { opacity: 0.55, cursor: "not-allowed" },
  guestNote: { fontSize: 11, lineHeight: 1.45, color: colors.textTertiary, marginTop: 8, textAlign: "center" },
};
